import React from "react";
import PortableText, { PortableTextBody } from "../PortableText";
import siteConfig from "../../config/siteConfig";
import Button, { ButtonSize } from "../Buttons/Button";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { PortableTextColorThemeProps } from "../PortableText/PortableText";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import BackgroundBlockPattern, { BackgroundBlockPatternType } from "../BackgroundBlockPattern";
import SanityImage from "../SanityImage";
import { isValidLink } from "../../utilities/links";
import { isNullOrWhiteSpace } from "../../shared/string";

enum ImageWithTextContentPosition {
    Left = "left",
    Right = "right",
    Auto = "auto"
}

export interface ImageWithTextProps {
    backgroundImage?: SanityImageSourceExt;
    backgroundPattern?: BackgroundBlockPatternType;
    title?: string;
    subtitle?: string;
    image?: SanityImageSourceExt;
    portableTextContent?: PortableTextBody;
    _rawPortableTextContent?: PortableTextBody;
    buttonLink?: LinkInfo;
    textPosition?: ImageWithTextContentPosition;
    portableTextColorThemeProps?: PortableTextColorThemeProps;
}

const ImageWithTextText = (props: ImageWithTextProps) => {
    const {
        buttonLink,
        subtitle,
        title,
        image,
        portableTextContent,
        _rawPortableTextContent,
        portableTextColorThemeProps,
        textPosition
    } = props;

    const {
        elementClasses,
        primaryButtonColor = "primary-light"
    } = portableTextColorThemeProps || {};

    const {
        h3Subheader: subtitleClasses = "text-subheader-primary"
    } = elementClasses || {};

    let classNames = "flex flex-col";

    if (textPosition === ImageWithTextContentPosition.Right) {
        classNames += " md:row-start-1 md:col-start-2";
    }
    else {
        // Reverse grid content using col-start-1
        classNames += " md:row-start-1 md:col-start-1";
    }

    if (textPosition === ImageWithTextContentPosition.Auto && !image) {
        // If no image and auto, center text content
        classNames += " max-w-4xl w-full mx-auto";
    }

    return (
        <div
            className={classNames}
        >
            {!isNullOrWhiteSpace(title) && (
                <h2 className="text-current mb-2 text-2xl lg:text-5xl">
                    {title}
                </h2>
            )}
            {!isNullOrWhiteSpace(subtitle) && (
                <h3 className={`text-xl ${subtitleClasses} uppercase font-bold mb-2 lg:mb-0`}>
                    {subtitle}
                </h3>
            )}

            {siteConfig.isGroup && (!isNullOrWhiteSpace(subtitle) || !isNullOrWhiteSpace(title)) &&
                <hr className="text-current h-divider-thick w-16 mt-4 mb-8 lg:mb-10" />
            }

            {(_rawPortableTextContent || portableTextContent) &&
                <PortableText
                    _rawBody={_rawPortableTextContent ?? portableTextContent}
                    addSections={false}
                    elementClasses={{
                        p: `${elementClasses?.p ?? ""} text-xl lg:text-2xl`,
                        li: `${elementClasses?.li ?? ""} mb-3 last:mb-0 text-xl font-bold`
                    }}
                />}

            {isValidLink(buttonLink) && (
                <Button
                    className="flex relative z-20 justify-center md:self-start mt-16 mb-6"
                    color={primaryButtonColor}
                    size={ButtonSize.Normal}
                    type="link"
                    {...buttonLink}>
                    {buttonLink.title}
                </Button>
            )}
        </div>
    );
};

const ImageWithText = (props: ImageWithTextProps) => {
    const {
        backgroundPattern,
        image,
        textPosition
    } = props;

    let gridClasses = "grid grid-flow-row-dense gap-24 py-8 lg:py-2 z-20";
    const asideClasses = "flex justify-center max-w-none relative px-6";

    let imageWrapperClasses = "flex items-start";

    if (textPosition === ImageWithTextContentPosition.Left ||
        textPosition === ImageWithTextContentPosition.Auto) {
        imageWrapperClasses += " justify-end";
    }

    if (textPosition === ImageWithTextContentPosition.Auto &&
        !image) {
        gridClasses += " grid-cols-1 justify-center";
    }
    else {
        gridClasses += " grid-cols-1 md:grid-cols-2";
    }

    return (
        <aside
            className={asideClasses}
        >
            {backgroundPattern && (
                <BackgroundBlockPattern
                    {...backgroundPattern}
                    margins={{
                        leftBlockMarginLeft: "24rem",
                        baseBlockMarginTop: "-8rem"
                    }}/>
            )}
            <div
                className={gridClasses}
                style={{width: "calc(1420px + 6rem)"}}
            >
                {image &&
                    <div className={imageWrapperClasses}>
                        <SanityImage
                            image={image}
                            loading="lazy"
                            width={710}
                            height={494}
                        />
                    </div>}
                <ImageWithTextText {...props}/>
            </div>
            {/* </div> */}
        </aside>
    );
};

export default ImageWithText;
